/**
 * The glossary: exercises with equipment, body part, how they are measured and a default rest time. Pure model
 * (validation, search, sorting) plus small repository functions on a storage `Backend`.
 */
import { ACTIVE_WORKOUT, META, newId, type Backend } from "./storage";

/** Default equipment keys (labels in texts.ts). Equipment is stored as a string, so the owner can add own values. */
export const EQUIPMENT = ["barbell", "dumbbell", "kettlebell", "sandbag", "sled", "machine", "bodyweight", "other"] as const;
export const BODY_PARTS = ["chest", "back", "legs", "shoulders", "arms", "core", "fullBody", "cardio"] as const;
/**
 * reps: weight × reps (bodyweight: reps only); timed: as many reps as possible in a time; hold: time only;
 * distance: metres, with the time taken.
 */
export const MEASURES = ["reps", "timed", "hold", "distance"] as const;

export type BodyPart = (typeof BODY_PARTS)[number];
export type Measure = (typeof MEASURES)[number];

export interface Exercise {
  id: string;
  name: string;
  /** one of EQUIPMENT or an own value typed by the owner */
  equipment: string;
  bodyPart: BodyPart;
  measure: Measure;
  /** default rest after a set, in seconds */
  restSec: number;
  notes: string;
  /** set instead of deleting when templates or sessions still use the exercise */
  hidden?: boolean;
}

/** An exercise being created (no id yet) or edited. */
export type ExerciseDraft = Omit<Exercise, "id" | "hidden"> & { id?: string };

export const DEFAULT_REST_SEC = 90;
export const MAX_REST_SEC = 600;

export function newExerciseDraft(partial: Partial<ExerciseDraft> = {}): ExerciseDraft {
  return { name: "", equipment: "barbell", bodyPart: "fullBody", measure: "reps", restSec: DEFAULT_REST_SEC, notes: "", ...partial };
}

/**
 * Variants (todo 16, owner 2026-10-30): an exercise measured in `reps` or `timed` can be done either way (sandbag
 * squats as 5 × 10 or as reps in 40 s: the same motion, so one exercise). Template, workout and session entries may
 * carry their own `measure`; without one, or when the exercise has no variants (any more), the glossary's applies.
 */
export const VARIANTS = ["reps", "timed"] as const;
export type Variant = (typeof VARIANTS)[number];

export const isVariant = (m: unknown): m is Variant => (VARIANTS as readonly unknown[]).includes(m);
export const hasVariants = (e: Pick<Exercise, "measure"> | undefined) => !!e && isVariant(e.measure);

/** The measure an entry is done with. */
export function entryMeasure(e: Pick<Exercise, "measure">, entry: { measure?: Measure }): Measure {
  return hasVariants(e) && isVariant(entry.measure) ? entry.measure : e.measure;
}

/** The exercise as an entry uses it: the same exercise with the entry's measure (what setFields & co. are given). */
export function asEntry<E extends Pick<Exercise, "measure">>(e: E | undefined, entry: { measure?: Measure }): E | undefined {
  if (!e) return e;
  const m = entryMeasure(e, entry);
  return m === e.measure ? e : { ...e, measure: m };
}

/**
 * The entry with `measure` set to `m`; stored only when it differs from the glossary's (an entry without one follows
 * the glossary). For an exercise unknown here the entry is returned as it is.
 */
export function withMeasure<T extends { measure?: Variant }>(entry: T, e: Pick<Exercise, "measure"> | undefined, m: Measure | undefined): T {
  if (!e) return entry;
  const { measure: _, ...rest } = entry;
  return (hasVariants(e) && isVariant(m) && m !== e.measure ? { ...rest, measure: m } : rest) as T;
}

/** Which values a set of this exercise has (used by the template editor and the workout log). */
export function setFields(e: Pick<Exercise, "equipment" | "measure">) {
  return {
    weight: e.equipment !== "bodyweight",
    reps: e.measure === "reps" || e.measure === "timed",
    duration: e.measure !== "reps",
    distance: e.measure === "distance",
  };
}

/** Which values a done set must have (checked when ticking a set and when saving an edited session). */
export function requiredFields(e: Pick<Exercise, "measure"> | undefined): ("weightKg" | "reps" | "durationSec" | "distanceM")[] {
  switch (e?.measure) {
    case "reps": case "timed": return ["reps"];
    case "hold": return ["durationSec"];
    case "distance": return ["distanceM"];
    default: return [];
  }
}

/** Lower case, no accents, ß as ss: for search and name comparison. */
export function foldText(s: string): string {
  return s.toLocaleLowerCase("de").normalize("NFD").replace(/\p{M}/gu, "").replace(/ß/g, "ss").replace(/\s+/g, " ").trim();
}

export type ExerciseError = "nameMissing" | "nameTaken" | "restInvalid" | "equipmentMissing";

/** Trims the text fields; the result is what gets stored. */
export function cleanDraft(d: ExerciseDraft): ExerciseDraft {
  return { ...d, name: d.name.replace(/\s+/g, " ").trim(), equipment: d.equipment.replace(/\s+/g, " ").trim(), notes: d.notes.trim() };
}

/** Errors of a draft against the existing glossary (names must be unique among the visible exercises). */
export function validateExercise(draft: ExerciseDraft, existing: Exercise[]): ExerciseError[] {
  const d = cleanDraft(draft);
  const errors: ExerciseError[] = [];
  if (!d.name) errors.push("nameMissing");
  else if (existing.some((e) => !e.hidden && e.id !== d.id && foldText(e.name) === foldText(d.name))) errors.push("nameTaken");
  if (!d.equipment) errors.push("equipmentMissing");
  if (!Number.isInteger(d.restSec) || d.restSec < 0 || d.restSec > MAX_REST_SEC) errors.push("restInvalid");
  return errors;
}

/** Alphabetical, German collation, case and accents ignored. */
export function sortExercises(list: Exercise[]): Exercise[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }) || a.id.localeCompare(b.id));
}

export interface ExerciseFilter {
  query?: string;
  equipment?: string;
  bodyPart?: BodyPart;
  /** include hidden exercises (they are left out by default) */
  hidden?: boolean;
}

/** Filters and sorts. Every word of the query must appear in the name. */
export function filterExercises(list: Exercise[], f: ExerciseFilter): Exercise[] {
  const words = foldText(f.query ?? "").split(" ").filter(Boolean);
  return sortExercises(list.filter((e) => {
    if (e.hidden && !f.hidden) return false;
    if (f.equipment && e.equipment !== f.equipment) return false;
    if (f.bodyPart && e.bodyPart !== f.bodyPart) return false;
    const name = foldText(e.name);
    return words.every((w) => name.includes(w));
  }));
}

/** Groups a sorted list by first letter, as Strong's list does. Names not starting with a letter go under "#". */
export function groupByLetter(sorted: Exercise[]): { letter: string; items: Exercise[] }[] {
  const groups: { letter: string; items: Exercise[] }[] = [];
  for (const e of sorted) {
    const first = foldText(e.name).charAt(0).toUpperCase();
    const letter = /\p{L}/u.test(first) ? first : "#";
    const last = groups[groups.length - 1];
    if (last?.letter === letter) last.items.push(e);
    else groups.push({ letter, items: [e] });
  }
  return groups;
}

/** The default equipment followed by the own values in use, alphabetical. */
export function equipmentOptions(list: Exercise[]): string[] {
  const own = new Set(list.map((e) => e.equipment).filter((x) => !(EQUIPMENT as readonly string[]).includes(x)));
  return [...EQUIPMENT, ...[...own].sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }))];
}

// --- storage ---

export const listExercises = (db: Backend) => db.getAll<Exercise>("exercises");

export type SaveResult = { ok: true; exercise: Exercise } | { ok: false; errors: ExerciseError[] };

/** Creates (no id) or updates an exercise after validating it. Updating keeps the hidden flag. */
export async function saveExercise(db: Backend, draft: ExerciseDraft): Promise<SaveResult> {
  const existing = await listExercises(db);
  const errors = validateExercise(draft, existing);
  if (errors.length) return { ok: false, errors };
  const d = cleanDraft(draft);
  const old = d.id ? existing.find((e) => e.id === d.id) : undefined;
  const exercise: Exercise = { ...d, id: d.id ?? newId(), ...(old?.hidden ? { hidden: true } : {}) };
  await db.put("exercises", exercise);
  return { ok: true, exercise };
}

/**
 * Templates and sessions refer to exercises as `exercises: [{ exerciseId }]` (todos 2 and 3 keep that shape).
 * Counts the records that use the exercise; the workout in progress counts as a session.
 */
export async function exerciseUsage(db: Backend, id: string): Promise<{ templates: number; sessions: number }> {
  type Uses = { id: string; exercises?: { exerciseId?: string }[] };
  const uses = (r: Uses | undefined) => !!r?.exercises?.some((x) => x.exerciseId === id);
  const count = async (c: "templates" | "sessions") => (await db.getAll<Uses>(c)).filter(uses).length;
  const active = await db.get<{ id: string; workout?: Uses }>(META, ACTIVE_WORKOUT);
  return { templates: await count("templates"), sessions: (await count("sessions")) + (uses(active?.workout) ? 1 : 0) };
}

/** Deletes an unused exercise; one still used by a template or session is only hidden, so history stays readable. */
export async function removeExercise(db: Backend, id: string): Promise<"deleted" | "hidden" | "missing"> {
  const e = await db.get<Exercise>("exercises", id);
  if (!e) return "missing";
  const use = await exerciseUsage(db, id);
  if (use.templates || use.sessions) {
    const hidden: Exercise = { ...e, hidden: true };
    await db.put("exercises", hidden);
    return "hidden";
  }
  await db.delete("exercises", id);
  return "deleted";
}

/** Shows a hidden exercise again. Fails with "nameTaken" if a visible exercise got its name meanwhile. */
export async function unhideExercise(db: Backend, id: string): Promise<SaveResult> {
  const e = await db.get<Exercise>("exercises", id);
  if (!e) return { ok: false, errors: [] };
  const errors = validateExercise(e, await listExercises(db));
  if (errors.length) return { ok: false, errors };
  const { hidden: _, ...visible } = e;
  await db.put("exercises", visible);
  return { ok: true, exercise: visible };
}
